import React, { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useMultiStepForm, StepConfig } from "../../components/forms/use-multi-step-form";
import { createMultiStepForm } from "../../components/forms/multi-step-form-builder";
import { Form } from "../../components/base/form";
import { AddressDetailsStep } from "./property-details/address-details-step";
import { PersonalDetailsStep } from "./personal-details/personal-details-step";
import { SolicitorSelectionStep } from "./solicitor-selection/solicitor-selection-step";
import { validateSolicitorSelection } from "./solicitor-selection/validation";
import { validateAddressDetails } from "./property-details/validation";
import { validatePersonalDetails } from "./personal-details/validation";
import {
  addressFieldsSchema,
  solicitorFieldsSchema,
  personalDetailsFieldsSchema,
  DEFAULT_VALUES,
  STEP_CONFIG,
} from "./schema";
import { InvisibleGoogleCaptcha, InvisibleGoogleCaptchaRef } from "../../utils/captcha/invisible-google-captcha";
import { ValuationFormSubmittedScreen } from "./valuation-form-submitted-screen";
import { GetCSRFToken } from "../../utils/csrf-token";
import { TrackEvent } from "../../utils/analytics";

// Define the complete form schema using shared field schemas
const ValuationsFormSchema = z
  .object({
    // Address fields
    ...addressFieldsSchema,

    // Solicitor fields
    ...solicitorFieldsSchema,

    // Personal details fields
    ...personalDetailsFieldsSchema,
  })
  .superRefine((data, ctx) => {
    // Use shared address validation function
    validateAddressDetails(data, ctx);

    // Use shared solicitor validation function
    validateSolicitorSelection(data, ctx);

    validatePersonalDetails(data, ctx);
  });

export type ValuationsFormSchemaType = z.infer<typeof ValuationsFormSchema>;

// Extend StepConfig to include UI-specific properties
export interface ValuationsStepConfig extends StepConfig<ValuationsFormSchemaType> {
  title: string;
}

interface ValuationsFormProps {
  isLoggedIn: boolean;
}

export const ValuationsForm: React.FC<ValuationsFormProps> = ({ isLoggedIn }) => {
  const captchaRef = useRef<InvisibleGoogleCaptchaRef>(null);
  const successScreenRef = useRef<HTMLDivElement>(null);
  const hasReported2ndStep = useRef(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Scroll to success screen when form is submitted
  useEffect(() => {
    if (isSubmitted && successScreenRef.current) {
      setTimeout(() => {
        successScreenRef.current?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
      }, 50);
    }
  }, [isSubmitted]);

  const steps = useMemo(
    () =>
      createMultiStepForm<ValuationsFormSchemaType, ValuationsStepConfig>(ValuationsFormSchema)
        .addStep(STEP_CONFIG.ADDRESS_DETAILS)
        .addStep(STEP_CONFIG.SOLICITOR_SELECTION)
        .addStep(STEP_CONFIG.PERSONAL_DETAILS)
        .build(),
    [],
  );

  const multiStepForm = useMultiStepForm<ValuationsFormSchemaType, ValuationsStepConfig>({
    steps,
    fullSchema: ValuationsFormSchema,
    defaultValues: DEFAULT_VALUES,
    onSubmit: async (data) => {
      try {
        setSubmissionError(null);
        setIsSubmitting(true);

        // Execute captcha to get token
        const captchaToken = await captchaRef.current?.execute();

        // Get CSRF token for request protection
        const csrfToken = await GetCSRFToken();

        // Map form data to backend ValuationRequest format - only send provided fields
        const requestBody: Record<string, unknown> = {
          FirstName: data.firstName,
          LastName: data.lastName,
          ContactPreference: data.contactPreference,
          PropertyPostcode: data.postcode,
          PropertyAddress: data.finalAddress,
          HasExistingSolicitor: data.hasExistingSolicitor === "yes",
          Solicitors:
            data.hasExistingSolicitor === "yes" && data.existingSolicitor
              ? [{ FirmId: data.existingSolicitor.firmId }]
              : data.selectedSolicitors.map((solicitor) => ({
                  FirmId: solicitor.firmId,
                  OrganisationId: solicitor.organisationId,
                })),
          CaptchaToken: captchaToken,
        };

        // Add optional fields only if provided
        if (data.email) requestBody.Email = data.email;
        if (data.phoneNumber) requestBody.Phone = data.phoneNumber;
        if (data.propertyType) requestBody.PropertyType = data.propertyType;
        if (data.bedrooms) requestBody.Bedrooms = parseInt(data.bedrooms, 10);
        if (data.sellingTimeframe) requestBody.Selling = data.sellingTimeframe;

        // Set suggestion preference based on user choice
        if (data.hasExistingSolicitor === "yes") {
          requestBody.SuggestionPreference = "existingSolicitor";
        } else if (data.hasExistingSolicitor === "no") {
          // Only include algorithm preference and chartered filter when user wants suggestions
          if (data.selectedAlgorithm) requestBody.SuggestionPreference = data.selectedAlgorithm.toLowerCase();
          if (data.charteredOnly) requestBody.CharteredFirms = data.charteredOnly;
        }

        const response = await fetch("/valuations", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            RequestVerificationToken: csrfToken.requestToken,
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorText = await response.text();
          if (response.status === 400) {
            if (errorText.includes("Captcha validation failed")) {
              setSubmissionError("CAPTCHA validation failed. Please try again.");
              captchaRef.current?.reset();
            } else if (errorText.includes("CSRF")) {
              setSubmissionError("Session expired. Please reload the page and try again.");
            } else {
              setSubmissionError("An error occurred while submitting your request. Please try again.");
            }
          } else {
            setSubmissionError("An error occurred while submitting your request. Please try again.");
          }
          setIsSubmitting(false);
          throw new Error(`Submission failed: ${response.status.toString()} ${response.statusText}`);
        }

        // Success - clear stored data and show success screen
        window.sessionStorage.removeItem("valuationPostcode");
        if (data.email) {
          window.sessionStorage.setItem("valuationEmail", data.email);
        }
        setIsSubmitting(false);
        setIsSubmitted(true);
        TrackEvent({ name: "valuation_lead_submitted", value: 100 });
      } catch {
        setIsSubmitting(false);
        if (!submissionError) {
          setSubmissionError("An error occurred while submitting your request. Please try again.");
        }
      }
    },
  });

  const { form } = multiStepForm;

  // Populate postcode from sessionStorage if available
  useEffect(() => {
    const storedPostcode = window.sessionStorage.getItem("valuationPostcode");
    if (storedPostcode) {
      form.setValue("postcode", storedPostcode);
    }
  }, [form]);

  // Report form view on mount (server-side reporting)
  useEffect(() => {
    fetch("/valuations/report/view", { method: "POST" }).catch((err: unknown) =>
      console.error("Failed to report form view:", err),
    );
  }, []);

  // Report 2nd step when entering solicitor selection (server-side reporting)
  // Step 0: Address Details, Step 1: Solicitor Selection, Step 2: Personal Details
  useEffect(() => {
    if (multiStepForm.currentStep === 1 && !hasReported2ndStep.current) {
      hasReported2ndStep.current = true;
      fetch("/valuations/report/2ndStep", { method: "POST" }).catch((err: unknown) =>
        console.error("Failed to report 2nd step:", err),
      );
    }
  }, [multiStepForm.currentStep]);

  // Show success screen after successful submission
  if (isSubmitted) {
    return (
      <div ref={successScreenRef}>
        <ValuationFormSubmittedScreen isLoggedIn={isLoggedIn} />
      </div>
    );
  }

  const renderStepContent = () => {
    return steps.map((step, index) => {
      switch (step.id) {
        case "address-details":
          return <AddressDetailsStep key={step.id} form={multiStepForm} stepConfig={step} index={index} />;
        case "personal-details":
          return (
            <PersonalDetailsStep
              key={step.id}
              form={multiStepForm}
              stepConfig={step}
              index={index}
              isSubmitting={isSubmitting}
              submissionError={submissionError}
            />
          );
        case "solicitor-selection":
          return <SolicitorSelectionStep key={step.id} form={multiStepForm} stepConfig={step} index={index} />;
        default:
          return null;
      }
    });
  };

  return (
    <div className="mx-auto bg-neutral-white w-full rounded-b-lg">
      <Form {...form}>
        <div className={"flex flex-col items-start justify-center"}>{renderStepContent()}</div>
      </Form>
      <InvisibleGoogleCaptcha ref={captchaRef} />
    </div>
  );
};
